import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { AdCodeCopyUrlButton } from 'components/AdCodeCopyUrlButton';
import { AdCodeQr } from 'components/AdCodeQr';
import { Alert } from 'components/Alert';
import { Button } from 'components/Button';
import { ConfirmModal } from 'components/ConfirmModal';
import { Pill } from 'components/ManagementCard';
import {
  ManagementDateRangeFilter,
  ManagementFilterBar,
  ManagementPagination,
} from 'components/ManagementFilters';
import { Snackbar } from 'components/Snackbar';
import { pageCardClasses } from 'components/AppLayout/styles';
import { routes } from 'constants/routes';
import { adCodeStrings, commonStrings } from 'constants/strings';
import { useAdCode, useAdCodeAttributions, useAdCodeMutations } from 'hooks';
import { useOrgPath } from 'hooks/useOrgPath';
import { toErrorMessage } from 'hooks/utils';
import {
  formatCount,
  formatRate,
  mediumLabel,
  statusLabel,
} from '../adCodeDisplay';
import { buildDefaultRange } from '../defaultRange';
import { NoActiveChannelAlert } from '../NoActiveChannelAlert';
import { AttributionsTable } from './AttributionsTable';

type LocationState = { snackbarMessage?: string };

export default function AdCodeDetail() {
  const navigate = useNavigate();
  const orgPathFor = useOrgPath();
  const location = useLocation();
  const { adCodeId } = useParams<{ adCodeId: string }>();
  const numericId = Number(adCodeId);

  const [snackbarMessage, setSnackbarMessage] = useState<string | undefined>();
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [isArchiveOpen, setIsArchiveOpen] = useState(false);
  const [actionError, setActionError] = useState<string | undefined>();
  // 一覧と同じ既定（直近30日）。詳細だけ全期間だと同じコードの数字が食い違う
  const [range, setRange] = useState(() => buildDefaultRange());
  const [excludeBlocked24h, setExcludeBlocked24h] = useState(false);

  const rangeParams = useMemo(
    () => ({
      ...(range.from ? { from: range.from } : {}),
      ...(range.to ? { to: range.to } : {}),
    }),
    [range],
  );
  const metricsParams = useMemo(
    () => ({
      ...rangeParams,
      ...(excludeBlocked24h ? { excludeBlocked24h: true } : {}),
    }),
    [rangeParams, excludeBlocked24h],
  );
  // 期間を切り替えた瞬間に render 中で1ページ目へ同期リセットする（一覧と同じ手法）。
  // 旧ページ番号のまま期間だけ変わると、絞った結果に存在しないページを要求してしまう
  const pageResetKey = JSON.stringify(rangeParams);
  const [pagination, setPagination] = useState({
    page: 1,
    resetKey: pageResetKey,
  });
  const page = pagination.resetKey === pageResetKey ? pagination.page : 1;
  const { adCode, loading, error } = useAdCode(numericId, metricsParams);
  // 帰属ユーザー一覧も指標タイルと同じ期間で絞る（同じ画面で数字を食い違わせない）
  const { data: attributions, loading: attributionsLoading } =
    useAdCodeAttributions(numericId, page, rangeParams);
  const { archive, saving } = useAdCodeMutations();

  useEffect(() => {
    const message = (location.state as LocationState | null)?.snackbarMessage;
    if (!message) return;
    setSnackbarMessage(message);
    setIsSnackbarOpen(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, navigate]);

  const handleCopied = () => {
    setSnackbarMessage(adCodeStrings.messages.urlCopied);
    setIsSnackbarOpen(true);
  };

  const handleArchive = (archived: boolean) => {
    setActionError(undefined);
    archive.mutate(
      { adCodeId: numericId, archived },
      {
        onSuccess: () => {
          setIsArchiveOpen(false);
          setSnackbarMessage(
            archived
              ? adCodeStrings.messages.archived
              : adCodeStrings.messages.unarchived,
          );
          setIsSnackbarOpen(true);
        },
        onError: (mutationError) => {
          // モーダルを開いたままだとエラー文言がオーバーレイの裏に隠れる
          setIsArchiveOpen(false);
          setActionError(
            toErrorMessage(mutationError, adCodeStrings.errors.archiveFailed),
          );
        },
      },
    );
  };

  const totalPage = attributions
    ? Math.max(1, Math.ceil(attributions.total / attributions.pageSize))
    : 0;

  const tiles = adCode
    ? [
        {
          label: adCodeStrings.metrics.click,
          value: formatCount(adCode.metrics.clickCount),
        },
        {
          label: adCodeStrings.metrics.registrationConfirmed,
          value: formatCount(adCode.metrics.confirmedCount),
        },
        {
          label: adCodeStrings.metrics.registrationEstimated,
          value: formatCount(adCode.metrics.estimatedCount),
          muted: true,
        },
        {
          label: adCodeStrings.metrics.registrationRate,
          value: formatRate(adCode.metrics.registrationRate),
        },
        {
          label: adCodeStrings.metrics.blocked24h,
          value: formatCount(adCode.metrics.blocked24hCount),
        },
        {
          label: adCodeStrings.metrics.blocked,
          value: formatCount(adCode.metrics.blockedCount),
        },
      ]
    : [];

  return (
    <>
      <section className={`w-full ${pageCardClasses.regular}`}>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => navigate(orgPathFor(routes.AD_CODES))}
        >
          {commonStrings.actions.backToList}
        </Button>

        {(error || actionError) && (
          <div className="mt-3">
            <Alert>{error ?? actionError}</Alert>
          </div>
        )}

        {loading && !adCode && (
          <p className="mt-4 text-sm text-muted">{commonStrings.loading}</p>
        )}

        {adCode && (
          <>
            <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 w-full">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="min-w-0 break-all font-display text-2xl text-ink">
                    {adCode.name}
                  </h1>
                  <Pill
                    tone={adCode.status === 'ACTIVE' ? 'success' : 'neutral'}
                  >
                    {statusLabel(adCode.status)}
                  </Pill>
                </div>
                <p className="mt-2 break-all text-sm text-ink">
                  <span className="text-muted">
                    {adCodeStrings.form.medium}:{' '}
                  </span>
                  <span>{mediumLabel(adCode.medium)}</span>
                </p>
                {adCode.memo && (
                  <p className="mt-1 text-sm text-muted">{adCode.memo}</p>
                )}
              </div>
              <div className="flex w-full flex-wrap items-center justify-between gap-2">
                <Button
                  type="button"
                  onClick={() =>
                    navigate(
                      orgPathFor(
                        routes.AD_CODES_EDIT.replace(
                          ':adCodeId',
                          String(numericId),
                        ),
                      ),
                    )
                  }
                >
                  {adCodeStrings.detail.edit}
                </Button>
                {adCode.status === 'ACTIVE' ? (
                  <Button
                    type="button"
                    variant="outline"
                    className="ml-auto"
                    onClick={() => setIsArchiveOpen(true)}
                    disabled={saving}
                  >
                    {adCodeStrings.detail.archive}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    className="ml-auto"
                    onClick={() => handleArchive(false)}
                    disabled={saving}
                  >
                    {adCodeStrings.detail.unarchive}
                  </Button>
                )}
              </div>
            </div>

            <NoActiveChannelAlert />

            <div className="mt-4 rounded-2xl bg-surface/70 p-4">
              {adCode.url ? (
                <div className="flex flex-wrap items-center gap-4">
                  <AdCodeQr
                    value={adCode.url}
                    fileName={`ad-code-${adCode.code}`}
                  />
                  <div className="min-w-[240px] flex-1">
                    <p className="text-[11px] text-muted">
                      {adCodeStrings.detail.urlLabel}
                    </p>
                    <p className="break-all font-mono text-sm text-ink">
                      {adCode.url}
                    </p>
                    <AdCodeCopyUrlButton
                      url={adCode.url}
                      idleLabel={adCodeStrings.detail.copyUrl}
                      className="mt-2"
                      onCopied={handleCopied}
                    />
                  </div>
                </div>
              ) : (
                <Alert>{adCodeStrings.detail.urlUnavailable}</Alert>
              )}
            </div>

            <div className="mt-4">
              <ManagementFilterBar>
                <ManagementDateRangeFilter
                  label={adCodeStrings.filters.period}
                  from={range.from}
                  to={range.to}
                  onChange={setRange}
                  onClear={() => setRange(buildDefaultRange())}
                />
                <label className="flex items-center gap-2 text-xs font-semibold text-ink">
                  <input
                    type="checkbox"
                    checked={excludeBlocked24h}
                    onChange={(event) =>
                      setExcludeBlocked24h(event.target.checked)
                    }
                  />
                  {adCodeStrings.filters.excludeBlocked24h}
                </label>
              </ManagementFilterBar>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-6">
              {tiles.map((tile) => (
                <div
                  key={tile.label}
                  className="rounded-2xl border border-border/60 bg-white px-3 py-2"
                >
                  <p className="text-[11px] text-muted">{tile.label}</p>
                  <p
                    className={`font-display text-lg tabular-nums ${
                      'muted' in tile && tile.muted
                        ? 'text-amber-700'
                        : 'text-ink'
                    }`}
                  >
                    {tile.value}
                  </p>
                </div>
              ))}
            </div>

            <h2 className="mt-6 text-sm font-semibold text-ink">
              {adCodeStrings.detail.attributions}
              {attributions && (
                <span className="ml-2 font-normal text-muted">
                  {formatCount(attributions.total)}
                  {commonStrings.countSuffix}
                </span>
              )}
            </h2>
            <div
              data-testid="ad-code-attributions-scroll"
              className="table-scroll mt-2 overflow-x-auto"
            >
              <AttributionsTable
                attributions={attributions?.items ?? []}
                loading={attributionsLoading}
              />
            </div>
            <div className="mt-3 flex justify-end">
              <ManagementPagination
                currentPage={page}
                totalPage={totalPage}
                loading={attributionsLoading}
                onPrevious={() =>
                  setPagination({
                    page: Math.max(1, page - 1),
                    resetKey: pageResetKey,
                  })
                }
                onNext={() =>
                  setPagination({
                    page: Math.min(totalPage, page + 1),
                    resetKey: pageResetKey,
                  })
                }
              />
            </div>
          </>
        )}
      </section>

      <ConfirmModal
        open={isArchiveOpen}
        textTitle={adCodeStrings.confirm.archiveTitle}
        textMessage={adCodeStrings.confirm.archiveMessage}
        textConfirm={adCodeStrings.confirm.archiveConfirm}
        textCancel={adCodeStrings.form.cancel}
        isLoading={saving}
        onConfirm={() => handleArchive(true)}
        onClose={() => setIsArchiveOpen(false)}
      />

      <Snackbar
        open={isSnackbarOpen}
        message={snackbarMessage ?? ''}
        onClose={() => setIsSnackbarOpen(false)}
      />
    </>
  );
}
